import { NextPageWithLayout } from '@/common/common-types'
import PageLayout from '@/components/page-layout/page-layout'
import PdImage from '@/components/pd-image/pd-image'
import retriveMessages, { LocaleType } from '@/messages/message'
import clsx from 'clsx'
import { GetStaticPropsContext } from 'next'
import { ReactElement, useMemo } from 'react'
import { getNewSupportData, getSupportCardLink, SupportData } from '@/service/new-support.service'
import { SEOData } from '@service/seo.service'
import useSWR from 'swr'
import { useLocale } from 'next-intl'
import Head from 'next/head'
import { withSEO } from '@/common/with-seo'
import Script from 'next/script'
import Image from 'next/image'
import { getLinkRelForHref } from '@/common/link-rel-helper'

interface NewSupportProps {
  supportData: SupportData
  seoData?: SEOData | null
}

const SALES_MARTLY_SCRIPT = 'https://plugin-code.salesmartly.com/js/project_543839_739839_1779179053.js'
const SALES_MARTLY_SCRIPT_ID = 'salesmartly-plugin'

const SUPPORT_ASSETS = {
  careLogo: '/images/support/pudu_care.svg',
  carePhoto: '/images/support/puducare_img.png',
  repair: '/images/support/icon_help.svg',
  docs: '/images/support/icon_doc.svg',
  fastResponse: '/images/support/icon-fast-response.svg',
  installService: '/images/support/icon-install-service.svg',
  maintenance: '/images/support/icon-maintenance.svg',
  quality: '/images/support/icon-quality.svg',
  onlineService: '/images/support/reply.svg',
  hotline: '/images/support/phone.svg',
}

const supportCopy = {
  'zh-CN': {
    cards: [
      {
        title: '线上自助报修',
        description: '在线申请维修，获取服务进度',
        cta: '我要报修',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[280px]',
      },
      {
        title: '文档与手册',
        description: '官方学习平台，获取产品信息',
        cta: '普渡学院',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description: '普渡服务团队提供机器人全生命周期一站式服务，从安装部署到售后支持全程护航，让您使用全程无忧',
      cta: '增值服务',
      features: [
        { title: '极速响应', icon: SUPPORT_ASSETS.fastResponse },
        { title: '标准化安装服务', icon: SUPPORT_ASSETS.installService },
        { title: '维修保养服务', icon: SUPPORT_ASSETS.maintenance },
        { title: '品质保障', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: '联系我们',
      onlineTitle: '在线客服',
      onlineLines: [
        '周一至周日：9：00-20：30（北京时间）',
        '服务邮箱：techservice@pudutech.com',
        '美国/加拿大：techservice.us@pudutech.com',
        '日本：japan_techgroup@pudutech.com',
      ],
      onlineCta: '联系在线客服',
      hotlineTitle: '热线服务',
      hotlineLines: ['中国区服务热线：', '400-0826-660', '周一至周日：9：00-20：30（北京时间）'],
    },
  },
  en: {
    cards: [
      {
        title: 'Online Repair Request',
        description: 'Submit a repair request online and track the service progress',
        cta: 'Request a Repair',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[280px]',
      },
      {
        title: 'Documents & Manuals',
        description: 'Official Learning Platform for Product Information',
        cta: 'Pudu Academy',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description:
        'Pudu Robotics provides comprehensive lifecycle services for robots, from installation and deployment to after-sales support, ensuring a seamless and worry-free experience throughout your entire journey.',
      cta: 'Service Plan',
      features: [
        { title: 'Fast Response', icon: SUPPORT_ASSETS.fastResponse },
        { title: 'Standardized Installation', icon: SUPPORT_ASSETS.installService },
        { title: 'Factory-Certified Maintenance', icon: SUPPORT_ASSETS.maintenance },
        { title: 'Guaranteed Quality', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Contact Us',
      onlineTitle: 'Online Customer Service',
      onlineLines: [
        'Monday to Sunday: 9:00–20:30 (Beijing Time)',
        'Technical Support Email: techservice@pudutech.com',
        'US/Canada: techservice.us@pudutech.com',
        'Japan: japan_techgroup@pudutech.com',
      ],
      onlineCta: 'Contact Online Support',
      hotlineTitle: 'Hotline',
      hotlineLines: ['1-855 756 5580', 'Monday to Sunday: 9:00–20:30 (Beijing Time)'],
    },
  },
  'zh-HK': {
    cards: [
      {
        title: '線上自助報修',
        description: '線上申請維修，取得服務進度',
        cta: '我要報修',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[280px]',
      },
      {
        title: '文檔與手冊',
        description: '官方學習平台，取得產品資訊',
        cta: '普渡學院',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description: '普渡服務團隊提供機器人全生命週期一站式服務，從安裝部署到售後支援全程護航，讓您使用全程無憂',
      cta: 'Service Plan',
      features: [
        { title: '極速響應', icon: SUPPORT_ASSETS.fastResponse },
        { title: '標準化安裝服務', icon: SUPPORT_ASSETS.installService },
        { title: '維修保養服務', icon: SUPPORT_ASSETS.maintenance },
        { title: '品質保障', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Contact Us',
      onlineTitle: '線上客服',
      onlineLines: [
        '週一至週日：9：00-20：30（UTC+8）',
        '服務郵箱：techservice@pudutech.com',
        '美國/加拿大：techservice.us@pudutech.com',
        '日本：japan_techgroup@pudutech.com',
      ],
      onlineCta: 'Contact Online Support',
      hotlineTitle: '熱線服務',
      hotlineLines: ['海外服務專線：', '1-855 756 5580', '週一至週日：9：00-20：30（UTC+8）'],
    },
  },
  jp: {
    cards: [
      {
        title: 'オンラインで簡単に修理を依頼',
        description: 'オンラインで修理をご依頼いただき、対応状況をいつでもご確認いただけます。',
        cta: '修理を依頼する',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[280px]',
      },
      {
        title: 'ドキュメント·マニュアル',
        description: '公式ラーニングプラットフォームで、製品情報をいつでもご確認いただけます。',
        cta: 'PUDU Academy（プデューアカデミー）',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description:
        'ロボットのライフサイクル全体をワンストップでサポートします。セットアップからアフターサービスまで、PUDUのサービスチームが安心・安定した運用を支えます。',
      cta: '付加価値サービス',
      features: [
        { title: '迅速対応', icon: SUPPORT_ASSETS.fastResponse },
        { title: '標準化されたセットアップサービス', icon: SUPPORT_ASSETS.installService },
        { title: 'リペア・メンテナンスサービス', icon: SUPPORT_ASSETS.maintenance },
        { title: '品質保証', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Contact Us',
      onlineTitle: 'オンラインカスタマーサポート',
      onlineLines: [
        '月曜日～日曜日：9:00～20:30（UTC+8）',
        'サービスお問い合わせ窓口 techservice@pudutech.com',
        '米国・カナダ：techservice.us@pudutech.com',
        '日本：japan_techgroup@pudutech.com',
      ],
      onlineCta: 'オンラインサポートにお問い合わせ',
      hotlineTitle: 'サービスホットライン',
      hotlineLines: ['海外サービス専用ダイヤル：1-855-756-5580', '受付時間：月曜日～日曜日 9:00～20:30（UTC+8）'],
    },
  },
  kr: {
    cards: [
      {
        title: '온라인 접수 하기',
        description: '온라인으로 A/S를 신청하고 서비스 진행 현황을 확인하세요',
        cta: 'A/S 접수하기',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[280px]',
      },
      {
        title: '문서 및 매뉴얼',
        description: '공식 학습 플랫폼에서 제품 및 기술 정보를 확인하세요',
        cta: '푸두 아카데미',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description:
        '푸두 서비스팀은 로봇의 설치 및 구축부터 유지보수까지 원스톱 서비스를 제공하여, 고객이 안심하고 로봇을 운영할 수 있도록 지원합니다',
      cta: 'Service Plan',
      features: [
        { title: '신속한 대응', icon: SUPPORT_ASSETS.fastResponse },
        { title: '표준화된 설치 서비스', icon: SUPPORT_ASSETS.installService },
        { title: '유지보수 서비스', icon: SUPPORT_ASSETS.maintenance },
        { title: '품질 보증', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Contact Us',
      onlineTitle: '온라인 고객센터',
      onlineLines: ['월요일-일요일 10:00-21:30 (UTC+9)', '서비스 이메일:', 'techservice@pudutech.com'],
      onlineCta: '온라인 지원 요청',
      hotlineTitle: '고객센터 전화 문의',
      hotlineLines: ['해외 고객센터:', '1-855-756-5580', '월요일-일요일 10:00-21:30 (UTC+9)'],
    },
  },
  de: {
    cards: [
      {
        title: 'Online-Selbstservice für Reparaturen',
        description: 'Online einen Reparaturauftrag stellen und den Servicestatus verfolgen',
        cta: 'Reparatur anfragen',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[320px]',
      },
      {
        title: 'Dokumentation und Handbücher',
        description: 'Offizielle Lernplattform für Produktinformationen',
        cta: 'Pudu Academy',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description:
        'Das Pudu-Serviceteam bietet einen umfassenden Rundum-Service über den gesamten Lebenszyklus Ihres Roboters, von der Installation bis zum Kundendienst für eine sorgenfreie Nutzung',
      cta: 'Service Plan',
      features: [
        { title: 'Schnelle Reaktionszeit', icon: SUPPORT_ASSETS.fastResponse },
        { title: 'Standardisierter Installationsservice', icon: SUPPORT_ASSETS.installService },
        { title: 'Reparatur- und Wartungsservice', icon: SUPPORT_ASSETS.maintenance },
        { title: 'Qualitätsgarantie', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Kontaktieren Sie uns',
      onlineTitle: 'Online-Kundenservice',
      onlineLines: [
        'Montag bis Sonntag: 9:00–20:30 Uhr (UTC +8:00)',
        'Service-E-Mail: techservice@pudutech.com',
        'USA/Kanada: techservice.us@pudutech.com',
        'Japan: japan_techgroup@pudutech.com',
      ],
      onlineCta: 'Service Support kontaktieren',
      hotlineTitle: 'Hotline-Service',
      hotlineLines: [
        'Service-Hotline im chin.Ausland:',
        '+86 1-855 756 5580',
        'Montag bis Sonntag: 9:00–20:30 Uhr (UTC +8:00)',
      ],
    },
  },
  es: {
    cards: [
      {
        title: 'Reporte de reparaciones en línea (autoservicio)',
        description: 'Solicite una reparación en línea y reciba actualizaciones sobre el estado del servicio',
        cta: 'Quiero reportar una reparación',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[340px]',
      },
      {
        title: 'Documentos y manuales',
        description: 'Plataforma oficial de aprendizaje para información sobre productos',
        cta: 'PUDU Academia',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description:
        'El equipo de servicio de Pudu ofrece un servicio integral durante todo el ciclo de vida del robot, desde la instalación y la puesta en marcha hasta el soporte posventa, lo que garantiza una experiencia sin preocupaciones para usted.',
      cta: 'Service Plan',
      features: [
        { title: 'Respuesta rápida', icon: SUPPORT_ASSETS.fastResponse },
        { title: 'Servicio de instalación estandarizado', icon: SUPPORT_ASSETS.installService },
        { title: 'Servicio de mantenimiento y reparación', icon: SUPPORT_ASSETS.maintenance },
        { title: 'Garantía de calidad', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Contáctenos',
      onlineTitle: 'Servicio de atención al cliente en línea',
      onlineLines: [
        'De lunes a domingo: de 9:00 a 20:30 (hora de Pekín)',
        'Correo electrónico de atención al cliente: techservice@pudutech.com',
        'EE. UU./Canadá: techservice.us@pudutech.com',
        'Japón: japan_techgroup@pudutech.com',
      ],
      onlineCta: 'Contactar Soporte en Línea',
      hotlineTitle: 'Servicio de atención telefónica',
      hotlineLines: [
        'Línea de atención telefónica internacional:',
        '1-855 756 5580',
        'De lunes a domingo: de 9:00 a 20:30 (hora de Pekín)',
      ],
    },
  },
  it: {
    cards: [
      {
        title: 'Richiesta di riparazione online',
        description: 'Richiedi una riparazione online e monitora lo stato del servizio',
        cta: 'Richiedi una riparazione',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[320px]',
      },
      {
        title: 'Documentazione e manuali',
        description: 'Piattaforma ufficiale di formazione, accedi alle informazioni sui prodotti',
        cta: 'PUDU Academy',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description:
        "Il servizio PUDU copre l'intero ciclo di vita del robot, dall'installazione e dalla messa in servizio fino al supporto post-vendita, per garantirvi un'esperienza d'uso senza pensieri",
      cta: 'Service Plan',
      features: [
        { title: 'Risposta rapida', icon: SUPPORT_ASSETS.fastResponse },
        { title: 'Servizio di installazione standardizzato', icon: SUPPORT_ASSETS.installService },
        { title: 'Servizio di manutenzione e riparazione', icon: SUPPORT_ASSETS.maintenance },
        { title: 'Garanzia di qualità', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Contattaci',
      onlineTitle: 'Servizio clienti online',
      onlineLines: [
        'Dal lunedì alla domenica: 09:00 - 20:30 (ora di Pechino)',
        'E-mail del servizio clienti: techservice@pudutech.com',
        'Stati Uniti / Canada: techservice.us@pudutech.com',
        'Giappone: japan_techgroup@pudutech.com',
      ],
      onlineCta: 'Contatta il Supporto Online',
      hotlineTitle: 'Servizio hotline',
      hotlineLines: [
        'Hotline internazionale:',
        '1-855 756 5580',
        'Dal lunedi alla domenica: 09:00 - 20:30 (ora di Pechino)',
      ],
    },
  },
  fr: {
    cards: [
      {
        title: 'Demande de réparation en ligne',
        description: 'Soumettez votre demande de réparation, suivez l’avancement du service',
        cta: 'Je demande une réparation',
        code: 'repair',
        icon: SUPPORT_ASSETS.repair,
        contentWidth: 'laptop:w-full laptop:max-w-[320px]',
      },
      {
        title: 'Documents et manuels',
        description: 'Plateforme officielle de formation, accéder aux informations produits',
        cta: 'Académie Pudu',
        code: 'academy',
        icon: SUPPORT_ASSETS.docs,
        contentWidth: 'laptop:w-full laptop:max-w-[404px]',
      },
    ],
    care: {
      description:
        'L’équipe Service Pudu vous accompagne à chaque étape du cycle de vie de votre robot, de l’installation au support après-vente, pour une expérience en toute sérénité',
      cta: 'Service Plan',
      features: [
        { title: 'Réponse rapide', icon: SUPPORT_ASSETS.fastResponse },
        { title: 'Installation standardisée', icon: SUPPORT_ASSETS.installService },
        { title: 'Maintenance et réparation', icon: SUPPORT_ASSETS.maintenance },
        { title: 'Qualité garantie', icon: SUPPORT_ASSETS.quality, iconBoxClass: 'w-[58px] h-14' },
      ],
    },
    contact: {
      title: 'Contactez-nous',
      onlineTitle: 'Service client en ligne',
      onlineLines: [
        'Du lundi au dimanche:9h00–20h30 (heure de Pékin)',
        'E-mail du service: techservice@pudutech.com',
        'États-Unis / Canada: techservice.us@pudutech.com',
        'Japon: japan_techgroup@pudutech.com',
      ],
      onlineCta: 'Contactez le support en ligne',
      hotlineTitle: 'Assistance téléphonique',
      hotlineLines: [
        'Hotline internationale :',
        '1-855 756 5580',
        'Du lundi au dimanche : 9h00–20h30 (heure de Pékin)',
      ],
    },
  },
}

const PUDU_CARE_ENTRY_LOCALES = ['zh-CN', 'en', 'jp']
const JP_PUDU_CARE_CTA = '付加価値サービス'

function getSupportCopy(locale: string) {
  return locale in supportCopy ? supportCopy[locale as keyof typeof supportCopy] : supportCopy.en
}

function shouldShowPuduCareEntry(locale: string) {
  return PUDU_CARE_ENTRY_LOCALES.includes(locale)
}

function getPuduCareEntryCta(locale: string, fallback: string) {
  if (locale === 'jp') return JP_PUDU_CARE_CTA
  return fallback
}

function openOnlineService() {
  if (typeof window === 'undefined') return

  const openChat = () => {
    const salesmartlyQueue = (window as any).ssq
    if (typeof salesmartlyQueue?.push === 'function') {
      salesmartlyQueue.push('chatOpen')
      return true
    }

    const salesmartlyEvent = (window as any).ssmEvent
    if (typeof salesmartlyEvent?.track === 'function') {
      salesmartlyEvent.track('chatOpen')
      return true
    }

    return false
  }

  if (openChat()) {
    return
  }

  document.getElementById(SALES_MARTLY_SCRIPT_ID)?.remove()
  document.getElementById('ss-chat')?.remove()
  document.querySelectorAll('script[src*="/chat/widget-v2/"]').forEach((script) => script.remove())
  delete (window as any).ssq
  delete (window as any).ssmEvent

  const openChatWhenReady = (remainingAttempts = 8) => {
    if (openChat() || remainingAttempts <= 0) return
    window.setTimeout(() => openChatWhenReady(remainingAttempts - 1), 500)
  }

  const script = document.createElement('script')
  script.id = SALES_MARTLY_SCRIPT_ID
  script.src = SALES_MARTLY_SCRIPT
  script.async = true
  script.addEventListener('load', () => openChatWhenReady(), { once: true })
  document.body.appendChild(script)
}

const NewSupport: NextPageWithLayout<NewSupportProps> = ({ supportData, seoData }) => {
  const locale = useLocale()
  const { data } = useSWR<SupportData>(getNewSupportData.swrkey(locale), () => getNewSupportData(locale), {
    fallbackData: supportData,
  })
  const copy = getSupportCopy(locale)
  const section1 = useMemo(() => {
    return {
      title: data?.title,
      description: data?.desc,
      backgroundImage: data?.top_media?.url,
    }
  }, [data])

  const actionCards = copy.cards.map((card) => ({
    ...card,
    href: getSupportCardLink(data, card.code),
  }))
  const showPuduCareEntry = shouldShowPuduCareEntry(locale)
  const careHref = `/${locale}/pudu-care`
  const careCta = getPuduCareEntryCta(locale, copy.care.cta)
  const careLogo = SUPPORT_ASSETS.careLogo
  const carePhoto = SUPPORT_ASSETS.carePhoto

  return (
    <div className="w-full h-fit bg-white">
      <Head>
        <title>{seoData?.title || data?.tab_title}</title>
      </Head>
      <Script id={SALES_MARTLY_SCRIPT_ID} src={SALES_MARTLY_SCRIPT} strategy="afterInteractive" />
      {/* section1 */}
      <div className={clsx('w-full h-fit bg-pdbg', 'pt-8 laptop:pt-37px')}>
        <div
          className={clsx(
            'w-full laptop:max-w-300 laptop:mx-auto',
            'flex flex-col laptop:flex-row justify-between laptop:items-center',
            'gap-y-3'
          )}
        >
          <div className={clsx('flex flex-col whitespace-pre-wrap px-6 laptop:px-0', 'gap-y-1 laptop:gap-y-4')}>
            <h1 className={clsx('text-24 leading-8 laptop:text-42 laptop:leading-[50px] font-semibold')}>
              {section1.title}
            </h1>
            <p className={clsx('text-14 leading-5 laptop:text-base laptop:leading-6')}>{section1.description}</p>
          </div>
          <div className={clsx('w-full h-[50vw] self-center laptop:w-336px laptop:h-263px')}>
            <PdImage src={section1.backgroundImage ?? ''} alt={section1.title ?? ''} width="100%" height="100%" />
          </div>
        </div>
      </div>

      <main className="w-full bg-white py-8 laptop:py-[120px]">
        <div className="w-full max-w-300 mx-auto px-6 laptop:px-0 flex flex-col gap-[60px] laptop:gap-[120px]">
          <section className="w-full grid grid-cols-1 laptop:grid-cols-2 gap-3 laptop:gap-[122px]">
            {actionCards.map((card) => (
              <SupportEntryCard key={card.title} card={card} />
            ))}
          </section>

          <div className="h-px w-full bg-[#d8d8d8] laptop:hidden" />

          <section className="w-full laptop:min-h-[853px] flex flex-col items-center gap-9 laptop:gap-12">
            <div className="w-full min-h-[136px] laptop:min-h-0 flex flex-col items-center gap-0 laptop:gap-6">
              <div className="w-[143px] h-9 laptop:w-[187px] laptop:h-[47px]">
                <Image
                  src={careLogo}
                  alt="PUDU CARE"
                  width={187}
                  height={47}
                  layout="responsive"
                  unoptimized
                  priority
                />
              </div>
              <p
                className={clsx(
                  'w-full max-w-[826px] mt-8 laptop:mt-0 text-center text-12 leading-4 laptop:text-18 laptop:leading-[26px] text-black/90 font-semibold laptop:font-normal',
                  locale === 'zh-CN' && 'laptop:max-w-[980px]'
                )}
              >
                {copy.care.description}
              </p>
              {showPuduCareEntry && (
                <a
                  href={careHref}
                  className="mt-[21px] laptop:mt-0 inline-flex items-center gap-0.5 text-12 leading-4 laptop:text-18 laptop:leading-[26px] text-[#0066ff]"
                >
                  <span>{careCta}</span>
                  <ArrowIcon className="w-3 h-3 laptop:w-6 laptop:h-6" />
                </a>
              )}
            </div>
            <div className="relative w-full max-w-[980px] [aspect-ratio:2/1] laptop:[aspect-ratio:unset] laptop:h-[462px] overflow-hidden">
              <Image src={carePhoto} alt="PUDU CARE service" layout="fill" objectFit="cover" unoptimized priority />
            </div>
            <ul className="w-full min-h-[368px] laptop:min-h-[148px] flex flex-col laptop:flex-row items-stretch laptop:items-center justify-center gap-0 laptop:gap-[68px]">
              {copy.care.features.map((item, index) => (
                <li
                  key={item.title}
                  className={clsx(
                    'relative min-w-0 flex flex-row laptop:flex-col items-start laptop:items-center justify-start laptop:justify-center gap-6 laptop:gap-3',
                    index === copy.care.features.length - 1 ? 'min-h-14' : 'min-h-[104px]',
                    'laptop:min-h-[148px] laptop:flex-1'
                  )}
                >
                  <div className={clsx('w-14 h-14', item.iconBoxClass)}>
                    <Image src={item.icon} alt="" width={item.iconBoxClass ? 58 : 56} height={56} unoptimized />
                  </div>
                  <p className="mt-4 laptop:mt-0 text-16 laptop:text-20 leading-6 laptop:leading-7 font-semibold text-black/90 text-left laptop:text-center">
                    {item.title}
                  </p>
                  {index < copy.care.features.length - 1 && (
                    <span className="absolute left-0 top-20 h-px w-full bg-[#d8d8d8] laptop:hidden" />
                  )}
                </li>
              ))}
            </ul>
          </section>

          <div className="h-px w-full bg-[#d8d8d8] laptop:hidden" />

          <section className="w-full flex flex-col items-center gap-10 laptop:gap-16">
            <h2 className="text-24 leading-8 laptop:text-36 laptop:leading-[44px] font-semibold text-black/90 text-center">
              {copy.contact.title}
            </h2>
            <div className="w-full grid grid-cols-1 laptop:grid-cols-2 gap-3 laptop:gap-16">
              <SupportContactCard
                icon={SUPPORT_ASSETS.onlineService}
                title={copy.contact.onlineTitle}
                lines={copy.contact.onlineLines}
                actionText={copy.contact.onlineCta}
                onActionClick={openOnlineService}
              />
              <SupportContactCard
                icon={SUPPORT_ASSETS.hotline}
                title={copy.contact.hotlineTitle}
                lines={copy.contact.hotlineLines}
              />
            </div>
          </section>
        </div>
      </main>
    </div>
  )
}

function SupportEntryCard({
  card,
}: {
  card: {
    title: string
    description: string
    cta: string
    href?: string
    icon: string
    contentWidth: string
  }
}) {
  const content = (
    <div className="w-full h-full min-h-[98px] laptop:min-h-64 bg-[#f2f3f5] flex items-center justify-center px-6 py-4 laptop:px-10 laptop:py-8">
      <div
        className={clsx(
          'flex w-full min-w-0 items-center gap-6 text-left',
          'laptop:flex-col laptop:items-center laptop:gap-3 laptop:text-center',
          card.contentWidth
        )}
      >
        <div className="w-15 h-15 laptop:w-24 laptop:h-24 shrink-0">
          <Image src={card.icon} alt="" width={96} height={96} layout="responsive" unoptimized />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2 laptop:w-full laptop:flex-none laptop:items-center laptop:gap-3">
          <div className="flex w-full flex-col gap-1 laptop:items-center">
            <h2 className="text-16 leading-5 laptop:text-20 laptop:leading-6 font-semibold text-black/90">
              {card.title}
            </h2>
            <p className="w-full text-12 leading-4 laptop:text-base laptop:leading-6 text-black/90">
              {card.description}
            </p>
          </div>
          <span className="inline-flex items-center gap-0.5 text-12 leading-4 laptop:text-base laptop:leading-6 text-[#297eff]">
            <span>{card.cta}</span>
            <ArrowIcon className="w-3 h-3 laptop:w-5 laptop:h-5" />
          </span>
        </div>
      </div>
    </div>
  )

  if (!card.href) {
    return <div className="w-full h-full">{content}</div>
  }

  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    <a
      href={card.href}
      target="_blank"
      rel={getLinkRelForHref(card.href, 'noopener noreferrer', '_blank')}
      className="block w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#297eff]"
    >
      {content}
    </a>
  )
}

function SupportContactCard({
  icon,
  title,
  lines,
  actionText,
  onActionClick,
}: {
  icon: string
  title: string
  lines: string[]
  actionText?: string
  onActionClick?: () => void
}) {
  return (
    <article className="w-full h-full min-h-[184px] laptop:min-h-[296px] bg-[#f2f3f5] flex items-center justify-center overflow-hidden px-6 py-6 laptop:px-10 laptop:py-8">
      <div className="w-full max-w-[460px] flex flex-col items-center justify-center gap-2 laptop:gap-3 text-center">
        <div className="w-16 h-16 laptop:w-24 laptop:h-24 shrink-0">
          <Image src={icon} alt="" width={96} height={96} layout="responsive" unoptimized />
        </div>
        <h3 className="text-[20px] leading-[24px] font-semibold text-black/90">{title}</h3>
        <div className="flex flex-col items-center gap-0.5 laptop:gap-1 text-[13px] leading-[20px] laptop:text-[16px] laptop:leading-[24px] font-normal text-black/90">
          {lines.map((line) => (
            <p key={line} className="w-full">
              {line}
            </p>
          ))}
        </div>
        {actionText && (
          <button
            type="button"
            onClick={onActionClick}
            className="inline-flex items-center justify-center gap-0.5 text-[14px] leading-[20px] laptop:text-[16px] laptop:leading-[24px] font-normal text-[#297eff]"
          >
            <span>{actionText}</span>
            <ArrowIcon className="w-3.5 h-3.5 laptop:w-5 laptop:h-5" />
          </button>
        )}
      </div>
    </article>
  )
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M8 5L13 10L8 15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['support'], locale as LocaleType)
    const supportData = await getNewSupportData(locale as LocaleType)

    return {
      props: {
        messages,
        supportData: supportData || null,
        seoData: supportData?.seoData,
      },
      revalidate: 60,
    }
  },
  () => '/support'
)

NewSupport.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      navbarMoveById="sstabs"
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}
export default NewSupport
